// src/components/Orders.js
import React, { useState, useEffect, useMemo } from 'react';
import config from '../config';
import '../styles/Orders.css';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Order details modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusComment, setStatusComment] = useState('');
  const [trackingId, setTrackingId] = useState('');
  const [courierName, setCourierName] = useState('');

  // Status history state
  const [statusHistory, setStatusHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ---- Helpers ----
  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMoney = (val) =>
    typeof val === 'number' ? `₹${val.toFixed(2)}` : `₹${val || 0}`;

  const getOrderItemCount = (order) =>
    (order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Collect distinct sellers for an order (based on item data)
  const getOrderSellers = (order) => {
    const sellers = new Set();
    (order.items || []).forEach((item) => {
      if (item.sellerName) {
        sellers.add(item.sellerName);
      } else if (item.sellerId) {
        sellers.add(`Seller ID: ${item.sellerId}`);
      }
    });
    if (sellers.size === 0) return '—';
    return Array.from(sellers).join(', ');
  };

  // ---- Auth helper (for admin token) ----
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('firebaseToken');
    if (!token) return {};
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  // ---- Fetch orders once on mount ----
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${config.baseUrl}/api/orders/allorders`);
        if (!response.ok) throw new Error('Failed to fetch orders');
        const data = await response.json();
        const ordersData = Array.isArray(data) ? data : [data];
        // Sort orders by creation date (latest first)
        const sortedOrders = ordersData.sort((a, b) => {
          const dateA = new Date(a.createdDate || 0);
          const dateB = new Date(b.createdDate || 0);
          return dateB - dateA; // Descending order (latest first)
        });
        setOrders(sortedOrders);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Error fetching orders');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  // ---- Fetch status history for selected order ----
  const fetchStatusHistory = async (orderId) => {
    try {
      setLoadingHistory(true);
      const response = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/status-history`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load status history');
      }

      const data = await response.json();
      setStatusHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(`Error loading status history: ${err.message}`);
      setStatusHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ---- Update order status ----
  const updateOrderStatus = async (orderId, newStatus) => {
    if (!orderId || !newStatus) return;

    // For SHIPPED status, validate required fields
    if (newStatus === 'SHIPPED') {
      if (!trackingId.trim()) {
        alert('Please enter a tracking ID for shipped orders');
        return;
      }
      if (!courierName.trim()) {
        alert('Please enter a courier partner name for shipped orders');
        return;
      }
    }

    if (!window.confirm(`Change order ${orderId} status to ${newStatus}?`))
      return;

    try {
      setUpdatingStatus(true);
      setError('');

      const commentToSend =
        statusComment.trim() ||
        `Status updated to ${newStatus} by admin`;

      const requestBody = {
        status: newStatus,
        comment: commentToSend,
      };

      // Add tracking details for SHIPPED status
      if (newStatus === 'SHIPPED') {
        requestBody.trackingId = trackingId.trim();
        requestBody.courierName = courierName.trim();
      }

      const response = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.message || 'Failed to update status');
      }

      // Clear form fields after successful update
      setStatusComment('');
      setTrackingId('');
      setCourierName('');

      // Refresh the orders list
      const ordersResponse = await fetch(`${config.baseUrl}/api/orders/allorders`);
      if (ordersResponse.ok) {
        const data = await ordersResponse.json();
        const ordersData = Array.isArray(data) ? data : [data];
        // Sort orders by creation date (latest first)
        const sortedOrders = ordersData.sort((a, b) => {
          const dateA = new Date(a.createdDate || 0);
          const dateB = new Date(b.createdDate || 0);
          return dateB - dateA; // Descending order (latest first)
        });
        setOrders(sortedOrders);

        // Update selected order
        const updatedOrder = ordersData.find(o => o.orderId === orderId);
        if (updatedOrder) {
          setSelectedOrder(updatedOrder);
        }
      }

      // Refresh status history
      await fetchStatusHistory(orderId);

    } catch (err) {
      console.error(err);
      setError(err.message || 'Error updating order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ---- Open order details modal ----
  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
    setStatusComment('');
    setTrackingId('');
    setCourierName('');
    fetchStatusHistory(order.orderId);
  };

  // ---- Close order details modal ----
  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
    setStatusHistory([]);
    setStatusComment('');
    setTrackingId('');
    setCourierName('');
  };

  // ---- Filtered orders (search + status) ----
  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return (orders || []).filter((order) => {
      let ok = true;

      if (term) {
        const customerName =
          (order.shippingAddress || '').split('\n')[0]?.toLowerCase() || '';
        const phone = (order.phoneNumber || '').toLowerCase();
        const id = (order.orderId || '').toString().toLowerCase();
        const customerId = (order.customerId || '').toString().toLowerCase();
        const razorpayOrderId =
          (order.razorpayOrderId || '').toString().toLowerCase();

        ok =
          id.includes(term) ||
          customerId.includes(term) ||
          phone.includes(term) ||
          customerName.includes(term) ||
          razorpayOrderId.includes(term);
      }

      if (ok && statusFilter !== 'ALL') {
        ok = (order.status || '') === statusFilter;
      }

      return ok;
    });
  }, [orders, searchTerm, statusFilter]);

  // ---- Download invoice ----
  const handleDownloadInvoice = async (orderId) => {
    if (!orderId) return;

    try {
      setDownloadingInvoiceId(orderId);
      setError('');

      const response = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/invoice`,
        {
          headers: {
            Accept: 'application/pdf', // explicitly ask for PDF
          },
        }
      );

      const contentType = response.headers.get('Content-Type') || '';

      if (!response.ok) {
        if (contentType.includes('application/json')) {
          const errJson = await response.json().catch(() => ({}));
          throw new Error(errJson.message || 'Failed to download invoice');
        }
        throw new Error('Failed to download invoice');
      }

      // Safety: backend accidentally returned JSON or text
      if (!contentType.startsWith('application/pdf')) {
        const text = await response.text();
        console.error('Unexpected invoice response:', text);
        throw new Error(
          'Server returned non-PDF data for invoice. Please check backend.'
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${orderId}-invoice.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error downloading invoice');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // ---- Download shipping label ----
  const handleDownloadShippingLabel = async (orderId) => {
    if (!orderId) return;

    try {
      setDownloadingInvoiceId(orderId); // Reuse same loading state for visual feedback
      setError('');

      const response = await fetch(
        `${config.baseUrl}/api/orders/${orderId}/shipping-label`,
        {
          headers: {
            Accept: 'application/pdf',
          },
        }
      );

      const contentType = response.headers.get('Content-Type') || '';

      if (!response.ok) {
        if (contentType.includes('application/json')) {
          const errJson = await response.json().catch(() => ({}));
          throw new Error(errJson.message || 'Failed to download shipping label');
        }
        throw new Error('Failed to download shipping label');
      }

      if (!contentType.startsWith('application/pdf')) {
        const text = await response.text();
        console.error('Unexpected label response:', text);
        throw new Error('Server returned non-PDF data for shipping label');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${orderId}-Shipping-Label.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error downloading shipping label');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // ---- Render states ----
  if (loading && orders.length === 0) {
    return <div className="loading">Loading orders...</div>;
  }

  if (error && orders.length === 0) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="orders-section">
      <div className="section-header">
        <h2>Order Management</h2>
        <p>Track and manage customer orders</p>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by Order ID, Customer, Phone, Tracking..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="SHIPPED">Shipped</option>
            <option value="DELIVERED">Delivered</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {error && orders.length > 0 && (
        <div className="error-inline">⚠ {error}</div>
      )}

      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Items &amp; Sellers</th>
              <th>Total</th>
              <th>Status</th>
              <th>Logistics</th>
              <th>Tracking</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No orders found
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => (
                <tr key={order.orderId}>
                  <td>{order.orderId}</td>
                  <td>
                    Customer ID: {order.customerId}
                    <br />
                    <span className="orders-customer-extra">
                      {((order.shippingAddress || '').split('\n')[0] ||
                        '').trim()}
                    </span>
                  </td>
                  <td>
                    {getOrderItemCount(order)} items
                    <div className="orders-seller-summary">
                      Sellers: {getOrderSellers(order)}
                    </div>
                    <ul className="items-list">
                      {(order.items || []).map((item, index) => (
                        <li key={index}>
                          Product ID: {item.productId}, Qty: {item.quantity},{' '}
                          Size: {item.size}, Color: {item.color}
                          {item.sellerName || item.sellerId ? (
                            <>
                              <br />
                              <span className="orders-item-seller">
                                Seller:{' '}
                                {item.sellerName ||
                                  `Seller ID: ${item.sellerId}`}
                              </span>
                            </>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>{formatMoney(order.totalAmount)}</td>
                  <td>
                    <span
                      className={`status-badge status-${(order.status || '')
                        .toLowerCase()
                        .trim()}`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td>{order.logisticsPartner || 'BlueDart'}</td>
                  <td>{order.razorpayOrderId || '—'}</td>
                  <td>{formatDateTime(order.createdDate)}</td>
                  <td>
                    <button
                      className="action-btn view"
                      onClick={() => openOrderDetails(order)}
                    >
                      View Details
                    </button>
                    <button
                      className="action-btn download"
                      onClick={() => handleDownloadInvoice(order.orderId)}
                      disabled={downloadingInvoiceId === order.orderId}
                    >
                      {downloadingInvoiceId === order.orderId ? 'Downloading...' : 'Invoice'}
                    </button>
                    <button
                      className="action-btn download label"
                      onClick={() => handleDownloadShippingLabel(order.orderId)}
                      disabled={downloadingInvoiceId === order.orderId}
                      style={{ marginLeft: '8px' }}
                    >
                      {downloadingInvoiceId === order.orderId ? 'Downloading...' : 'Label'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="order-modal-overlay" onClick={closeOrderModal}>
          <div className="order-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="order-modal-header">
              <div>
                <h2>Order Details - {selectedOrder.orderId}</h2>
                <p>{formatDateTime(selectedOrder.createdDate)}</p>
              </div>
              <div className="order-modal-header-right">
                <span
                  className={`status-badge status-${(selectedOrder.status || '')
                    .toLowerCase()
                    .trim()}`}
                >
                  {selectedOrder.status}
                </span>
                <button className="close-btn" onClick={closeOrderModal}>
                  ✕
                </button>
              </div>
            </div>

            <div className="order-modal-body">
              {/* Customer Information */}
              <div className="order-section">
                <h3>Customer Information</h3>
                <div className="order-info-grid">
                  <div>
                    <strong>Customer ID:</strong> {selectedOrder.customerId}
                  </div>
                  <div>
                    <strong>Phone:</strong> {selectedOrder.phoneNumber || '-'}
                  </div>
                  <div className="full-width">
                    <strong>Shipping Address:</strong>
                    <br />
                    {selectedOrder.shippingAddress || 'No address provided'}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="order-section">
                <h3>Order Items ({getOrderItemCount(selectedOrder)} items)</h3>
                <div className="order-items-list">
                  {(selectedOrder.items || []).map((item, index) => (
                    <div key={index} className="order-item">
                      <div className="item-image">
                        {item.primaryImageUrl ? (
                          <img src={item.primaryImageUrl} alt={item.productName} />
                        ) : (
                          <div className="no-image">No Image</div>
                        )}
                      </div>
                      <div className="item-details">
                        <h4>{item.productName || 'Unknown Product'}</h4>
                        <p>SKU: {item.sku || 'N/A'}</p>
                        <p>Size: {item.sizeName || 'N/A'}</p>
                        <p>Color: {item.color || 'N/A'}</p>
                        <p>Quantity: {item.quantity}</p>
                        <p>Price: {formatMoney(item.price)} each</p>
                        <p>Total: {formatMoney(item.price * item.quantity)}</p>
                        {item.sellerName && (
                          <p className="seller-info">Seller: {item.sellerName}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Total */}
              <div className="order-section">
                <h3>Order Total</h3>
                <div className="order-total">
                  <strong>Total Amount: {formatMoney(selectedOrder.totalAmount)}</strong>
                </div>
              </div>

              {/* Status History */}
              <div className="order-section">
                <h3>Status History</h3>
                {loadingHistory ? (
                  <p>Loading status history...</p>
                ) : statusHistory.length === 0 ? (
                  <p>No status updates recorded yet.</p>
                ) : (
                  <div className="status-history">
                    {statusHistory.map((h, idx) => (
                      <div key={idx} className="status-history-item">
                        <div className="status-history-main">
                          <span
                            className={`status-badge status-${(h.status || '')
                              .toLowerCase()}`}
                          >
                            {h.status}
                          </span>
                          <span className="status-history-meta">
                            {h.changedByName || 'System'} • {formatDateTime(h.createdAt)}
                          </span>
                        </div>
                        {h.comment && (
                          <div className="status-history-comment">{h.comment}</div>
                        )}
                        {h.trackingId && (
                          <div className="status-history-tracking">
                            Tracking ID: {h.trackingId}
                            {h.courierName && ` (${h.courierName})`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Order Actions */}
              <div className="order-section">
                <h3>Update Order Status</h3>

                {error && <div className="error-message">{error}</div>}

                <div className="status-update-form">
                  <div className="form-group">
                    <label htmlFor="statusComment">Status Comment (optional)</label>
                    <textarea
                      id="statusComment"
                      placeholder="Enter a comment about this status update..."
                      value={statusComment}
                      onChange={(e) => setStatusComment(e.target.value)}
                      rows="3"
                    />
                  </div>

                  <div className="tracking-fields" style={{
                    display: 'block',
                    opacity: 1,
                    marginBottom: '15px'
                  }}>
                    <div className="form-group">
                      <label htmlFor="trackingId">
                        Tracking ID <span className="required-note">(Required for Shipped status)</span>
                      </label>
                      <input
                        id="trackingId"
                        type="text"
                        placeholder="Enter tracking/AWB number"
                        value={trackingId}
                        onChange={(e) => setTrackingId(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="courierName">
                        Courier Partner <span className="required-note">(Required for Shipped status)</span>
                      </label>
                      <select
                        id="courierName"
                        value={courierName}
                        onChange={(e) => setCourierName(e.target.value)}
                      >
                        <option value="">Select courier partner</option>
                        <option value="DTDC">DTDC</option>
                        <option value="Delhivery">Delhivery</option>
                        <option value="Blue Dart">Blue Dart</option>
                        <option value="FedEx">FedEx</option>
                        <option value="Aramex">Aramex</option>
                        <option value="Ecom Express">Ecom Express</option>
                        <option value="XpressBees">XpressBees</option>
                        <option value="Shadowfax">Shadowfax</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="status-buttons">
                    <button
                      className="status-btn confirmed"
                      disabled={updatingStatus}
                      onClick={() => updateOrderStatus(selectedOrder.orderId, 'CONFIRMED')}
                    >
                      Confirm Order
                    </button>
                    <button
                      className="status-btn processing"
                      disabled={updatingStatus}
                      onClick={() => updateOrderStatus(selectedOrder.orderId, 'PROCESSING')}
                    >
                      Mark Processing
                    </button>
                    <button
                      className="status-btn shipped"
                      disabled={updatingStatus}
                      onClick={() => updateOrderStatus(selectedOrder.orderId, 'SHIPPED')}
                    >
                      Mark Shipped
                    </button>
                    <button
                      className="status-btn delivered"
                      disabled={updatingStatus}
                      onClick={() => updateOrderStatus(selectedOrder.orderId, 'DELIVERED')}
                    >
                      Mark Delivered
                    </button>
                    <button
                      className="status-btn cancelled"
                      disabled={updatingStatus}
                      onClick={() => updateOrderStatus(selectedOrder.orderId, 'CANCELLED')}
                    >
                      Cancel Order
                    </button>
                  </div>

                  {updatingStatus && (
                    <div className="updating-status">Updating order status...</div>
                  )}
                </div>

                <div className="modal-actions">
                  <button
                    className="action-btn download"
                    onClick={() => handleDownloadInvoice(selectedOrder.orderId)}
                    disabled={downloadingInvoiceId === selectedOrder.orderId}
                  >
                    {downloadingInvoiceId === selectedOrder.orderId
                      ? 'Downloading...'
                      : 'Download Invoice'}
                  </button>
                  <button
                    className="action-btn download label"
                    onClick={() => handleDownloadShippingLabel(selectedOrder.orderId)}
                    disabled={downloadingInvoiceId === selectedOrder.orderId}
                    style={{ marginLeft: '12px' }}
                  >
                    {downloadingInvoiceId === selectedOrder.orderId
                      ? 'Downloading...'
                      : 'Shipping Label'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
